import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import CategoryTabs from '@/components/base/CategoryTabs';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { getComicsByCategory, comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    let result = getComicsByCategory(currentSlug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    setFilteredComics(result);
  }, [currentSlug, sortBy]);

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const categoryName = currentCategory?.name || '全部';
  const categoryTitle = currentSlug === 'all'
    ? '全部韩漫 - 虚空韩漫网'
    : `${categoryName}韩漫 - 虚空韩漫网`;

  return (
    <>
      <SEOHead
        title={`${categoryTitle}_免费韩漫在线阅读_高清下拉式韩漫全集`}
        description={`虚空韩漫网${categoryName}韩漫分类，提供高清下拉式韩漫全集免费阅读。收录${filteredComics.length}部${categoryName}韩漫，每日更新，正版合规。`}
        keywords={`${categoryName}韩漫, 虚空韩漫网, 免费韩漫, 韩漫在线阅读, 高清韩漫, 韩国漫画免费读`}
        canonicalUrl={`https://xukonghanman.com/category/${currentSlug}`}
        ogType="website"
        breadcrumbItems={[
          { name: '首页', url: 'https://xukonghanman.com/' },
          { name: categoryName, url: `https://xukonghanman.com/category/${currentSlug}` },
        ]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { name: '首页', url: '/' },
            { name: categoryName, url: `/category/${currentSlug}` },
          ]}
        />

        <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {categoryName}韩漫
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共找到 <strong>{filteredComics.length}</strong> 部作品
              </p>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              {[
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key as 'latest' | 'popular' | 'rating')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === s.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <CategoryTabs />

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 mt-6">
              {filteredComics.map((comic) => (
                <div key={comic.id} data-product-shop>
                  <ComicCard comic={comic} />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无作品，请浏览其他分类</p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-apps-line"></i>
                查看全部漫画
              </Link>
            </div>
          )}
        </section>

        <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-foreground-900 font-heading mb-3">
              关于{categoryName}韩漫
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {currentSlug === 'all'
                ? '虚空韩漫网收录了涵盖恋爱、奇幻、动作、悬疑、搞笑、剧情、恐怖、日常、历史等全品类韩漫作品。所有漫画均为高清下拉式阅读模式，正版合规。无论你偏好哪种类型，都能在这里找到心仪的作品。'
                : `虚空韩漫网${categoryName}分类精心收录了多部高品质${categoryName}类韩漫作品。每部漫画都经过编辑审核，确保内容质量。下拉式阅读设计让您畅享沉浸式的${categoryName}韩漫阅读体验。`}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}